import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { RegistrationContext } from '../../../context/registration';
import { useToasts } from 'react-toast-notifications';
import Select from 'react-select';

import {
  searchMedicalCenters,
  searchMedicalProfessionals,
  setMedicalProfessional,
  fetchCampaignContext,
} from '~/services/registration';
import states from '~/shared/states.json';

import { CheckBox } from '~/shared/components/form/CheckBox';
import { ProgressBar } from '../ProgressBar';
import {
  Container,
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Form,
  FormGroup,
  Input,
  Label,
  Spinner,
} from 'reactstrap';

import { SelectOption } from '~/types/globals';
import { Loading } from '../../../shared/components/Loading';

export const MedicalProfessional = ({}: any) => {
  const [{ campaign }, registrationDispatch] = useContext(RegistrationContext);
  const [isSelfCampaign, setIsSelfCampaign] = useState<boolean>(true);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [medicalCenters, setMedicalCenters] = useState();
  const [selectedMedicalCenter, setSelectedMedicalCenter] = useState();
  const [medicalCenterOptions, setMedicalCenterOptions] = useState<
    SelectOption[]
  >([]);
  const [medicalProfessionals, setMedicalProfessionals] = useState();
  const [selectedMedicalProfessional, setSelectedMedicalProfessional] =
    useState();
  const [medicalProfessionalOptions, setMedicalProfessionalOptions] = useState<
    SelectOption[]
  >([]);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [consent, setConsent] = useState<boolean>(false);
  const { addToast } = useToasts();
  const history = useHistory();

  const getCampaign = async () => {
    const resp = await fetchCampaignContext();
    console.log('resp: ', resp);

    if (resp['detail'] === 'Invalid token.') {
      history.push('/login');
    }

    registrationDispatch({
      type: 'set_campaign',
      campaign: resp.campaign,
    });
  };

  useEffect(() => {
    if (campaign) {
      setLoading(false);
    } else {
      getCampaign();
    }
  }, [campaign]);

  const getMedicalCenters = async () => {
    setLoadingOptions(true);
    const resp = await searchMedicalCenters(state);
    setMedicalCenters(resp);
    const opts = resp.map((medCenter, i) => {
      return {
        label: medCenter.name,
        value: medCenter.id,
      };
    });
    setMedicalCenterOptions(opts);
    setLoadingOptions(false);
  };

  useEffect(() => {
    if (state) {
      getMedicalCenters();
    }
  }, [state]);

  const getMedicalProfessionals = async () => {
    setLoadingOptions(true);
    const medCenterId = selectedMedicalCenter.id;
    const resp = await searchMedicalProfessionals(medCenterId);
    console.log('resp: ', resp);
    setMedicalProfessionals(resp);
    const opts = resp.map((prof, i) => {
      const label = `${prof.first_name} ${prof.last_name}`;
      return {
        label,
        value: prof.id,
      };
    });
    setMedicalProfessionalOptions(opts);
    setLoadingOptions(false);
  };

  useEffect(() => {
    if (selectedMedicalCenter) {
      getMedicalProfessionals();
    }
  }, [selectedMedicalCenter]);

  const handleNext = async () => {
    setSaving(true);
    try {
      const medCenterId = selectedMedicalCenter.id;
      const personId = selectedMedicalProfessional.id;
      const resp = await setMedicalProfessional(medCenterId, personId);
      registrationDispatch({
        type: 'set_patient',
        patient: resp,
      });
      history.push('/get-started/financials/cost');
    } catch (e) {
      addToast('There was a problem saving', { appearance: 'error' });
    }
  };

  const onSubmit = async (data) => {
    console.log('data: ', data);
  };

  if (loading) {
    return <Loading />;
  }

  const title = isSelfCampaign
    ? `We offer 100% fraud-free fundraising by verifying our clients' medical conditions with their medical providers.`
    : `We offer 100% fraud-free fundraising by verifying our clients' medical conditions with their medical providers.`;

  const fieldTitle = isSelfCampaign
    ? `Let's find your medical provider`
    : `Let's find their medical provider`;

  const pronoun = isSelfCampaign ? 'your' : 'their';

  const isValid =
    selectedMedicalCenter && selectedMedicalProfessional && consent && !saving;

  return (
    <Container>
      <ProgressBar selectedStep='condition' />

      <Form>
        <Row className='justify-content-center py-5'>
          <Col md='6'>
            <div className='text-center'>
              <h3>{title}</h3>
              <p>
                Their medical provider may be a social worker, physician,
                financial coordinator, case manager, or rehabilitation
                specialist.
              </p>
            </div>

            <Card
              className='box-shadow my-5'
              style={{
                maxWidth: '800px',
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              <CardBody>
                <h4>{fieldTitle}</h4>
                <FormGroup>
                  <Label>{`Where is ${pronoun} Medical Professional located?`}</Label>
                  <Input
                    type='select'
                    name='state'
                    id='state'
                    onChange={(e) => setState(e.target.value)}
                  >
                    <option>Select State</option>
                    {Object.entries(states).map(([accronym, label], i) => (
                      <option key={i} value={label}>
                        {label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                {medicalCenters && medicalCenterOptions && (
                  <FormGroup>
                    <Label>{`What is ${pronoun} Medical Professional's medical center?`}</Label>
                    <Select
                      options={medicalCenterOptions}
                      onChange={(opt) => {
                        const match = medicalCenters.find(
                          (x) => x.id === opt.value
                        );
                        setSelectedMedicalCenter(match);
                      }}
                    />
                  </FormGroup>
                )}

                {medicalProfessionals && medicalProfessionalOptions && (
                  <FormGroup>
                    <Label>{`Who is ${pronoun} Medical Professional?`}</Label>
                    <Select
                      options={medicalProfessionalOptions}
                      onChange={(opt) => {
                        const match = medicalProfessionals.find(
                          (x) => x.id === opt.value
                        );
                        setSelectedMedicalProfessional(match);
                      }}
                    />
                  </FormGroup>
                )}
                <Row>
                  <Col>
                    {loadingOptions && (
                      <Spinner color='primary' size='sm'>
                        Loading
                      </Spinner>
                    )}
                    {loadingOptions && <div>Loading</div>}
                  </Col>
                </Row>

                <FormGroup check className='mb-3 ps-0'>
                  <CheckBox
                    name='consent'
                    label='I give consent to their health care professional to provide information to Help Hope Live confirming their medical and financial need for the purpose of fundraising.'
                    value={consent}
                    onChange={(e) => setConsent(!consent)}
                  />
                </FormGroup>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <div className='text-center mb-5'>
          <Button
            color='outline-dark'
            disabled={saving}
            onClick={() => history.push('/get-started/condition/when')}
          >
            Back
          </Button>
          <Button
            color='primary'
            disabled={!isValid || saving}
            onClick={handleNext}
          >
            {saving ? `Saving` : `Next`}
          </Button>

          <div className='mt-4'>
            <Button color='link'>Skip for Now</Button>
            <p>
              You will be required to provide this information in order to
              receive any funds raised for this campaign.
            </p>
          </div>
        </div>
      </Form>
    </Container>
  );
};
